import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpStatusCodes, HttpStatusCodesHelper } from '@shared/http-status-codes';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { InterceptorMethods } from './interceptor-methods';

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
    private readonly MAX_RETRIES = 3;
    private readonly BASE_RETRY_DELAY_MS = 1500;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!InterceptorMethods.shouldIntercept(request)) {
            return next.handle(request);
        }

        if (request.method !== 'GET') {
            return next.handle(request);
        }

        return this.handleRequestWithRetry(request, next);
    }

    private handleRequestWithRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            retry({
                count: this.MAX_RETRIES,
                delay: (error: HttpErrorResponse, retryCount) => {
                    if (!this.isRetryableError(error)) {
                        throw error;
                    }

                    const delayMs = this.BASE_RETRY_DELAY_MS * retryCount;
                    console.warn(
                        `Network error detected. Retrying GET request (${retryCount}/${this.MAX_RETRIES}) ` +
                            `after ${delayMs}ms delay: ${request.url}`,
                    );

                    return timer(delayMs);
                },
            }),
            catchError((error: HttpErrorResponse) => {
                if (HttpStatusCodesHelper.fetchFromHttpErrorResponse(error) === HttpStatusCodes.NetworkError) {
                    console.error(`Max retry attempts exceeded: ${request.url}`);
                    (error as any).retryExhausted = true;
                }
                return throwError(() => error);
            }),
        );
    }

    /**
     * Determines if an error should be retried.
     * Retries on transient/network-related failures:
     * - status === 0 (network offline, CORS failure, etc.)
     * - status === 408 (request timeout)
     *
     * Does NOT retry on:
     * - 401 (already handled by refresh)
     * - 403 (forbidden)
     * - 429 (rate limit - avoid hammering)
     * - status >= 500 (server errors: 500, 502, 503, 504)
     * - Other client errors (4xx except 401)
     */
    private isRetryableError(error: HttpErrorResponse): boolean {
        const status = error.status;

        // Network errors (offline, CORS failure, etc.)
        if (status === HttpStatusCodes.NetworkError) {
            return true;
        }

        // Request timeout
        if (status === HttpStatusCodes.RequestTimeout) {
            return true;
        }

        return false;
    }
}
